import React, { useState } from "react";
import { Edit2, Clock, Trash2 } from "react-feather";
import { useTheme } from "../../theme/ThemeContext";
import { groupCurrentBills, daysUntilDue, getBillDisplayStatus, billKey, paidFromName } from "../../utils/bills";
import { scheduleLabel, frequencyOf, isRepeatingBill } from "../../utils/billFrequency";
import { Card, Chip, ChipRow, StatusPill, Sheet, Button, Empty, useMoney, formatDate } from "../ui";
import AddEntrySheet from "./AddEntrySheet";
import { usePayBill, billAmountText } from "./PaidAmountSheet";

const FILTERS = ["All", "Due", "Pending", "Upcoming", "Paid"];

// Unpaid and the date has passed: Due. Within the next 7 days: Pending, with the
// days left. Further out: Upcoming.
const dueText = (bill) => {
  if (bill.status === "Paid") return `Paid · ${formatDate(bill.dueDate)}`;
  const d = daysUntilDue(bill.dueDate);
  if (d < 0) return `Due since ${formatDate(bill.dueDate)} · ${Math.abs(d)} day${d === -1 ? "" : "s"} late`;
  if (d === 0) return "Pending · due today";
  if (d <= 7) return `Pending · ${d} day${d === 1 ? "" : "s"} left · ${formatDate(bill.dueDate)}`;
  return `Upcoming · ${formatDate(bill.dueDate)} · in ${d} days`;
};

// Entries made automatically from an investment or loan say where they came from.
export const sourceNote = (bill) => {
  if (bill.sourceKind === "loan") return "EMI from your loan";
  if (bill.sourceKind === "asset") return bill.type === "Investments" ? "From your investment" : "From your holdings";
  return "";
};

// Read-only view: the bill's details, plus the three actions the user asked for -
// Edit, History and Delete - kept separate from the edit form itself.
const BillView = ({ bill, data, onMarkPaid, onEdit, onHistory, onDelete }) => {
  const { tokens } = useTheme();
  const money = useMoney();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isPaid = bill.status === "Paid";
  const { repeatUnit, repeatEvery } = frequencyOf(bill);

  const row = (label, value) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderTop: `1px solid ${tokens.surfaceBorder}` }}>
      <span style={{ fontSize: 13, color: tokens.textMuted }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 600, color: tokens.textPrimary }}>{value}</span>
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontFamily: "inherit", fontSize: 26, fontWeight: 700, color: tokens.textPrimary }}>{billAmountText(bill, money)}</span>
        <StatusPill label={getBillDisplayStatus(bill)} />
      </div>
      <div style={{ fontSize: 13, color: tokens.textMuted, marginBottom: 4 }}>
        {bill.type}{!!bill.autopay && " · Autopay"}{!!bill.variableAmount && " · amount changes each time"}
      </div>

      {row("Due date", formatDate(bill.dueDate))}
      {row("Frequency", scheduleLabel(repeatUnit, repeatEvery))}
      {!!bill.sourceKind && row("Added by", sourceNote(bill))}
      {row("Payment method", bill.autopay ? "Autopay" : "Manual")}
      {!!bill.paidFromKind && row("Paid from", paidFromName(bill, data))}
      {!!bill.description && row("Description", bill.description)}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 20 }}>
        <Button kind={isPaid ? "ghost" : "primary"} onClick={onMarkPaid}>
          {isPaid ? "Mark as unpaid" : "Mark as paid"}
        </Button>
        <div style={{ display: "flex", gap: 10 }}>
          <Button kind="ghost" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }} onClick={onEdit}>
            <Edit2 size={15} /> Edit
          </Button>
          <Button kind="ghost" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }} onClick={onHistory}>
            <Clock size={15} /> History
          </Button>
        </div>
        {!confirmDelete ? (
          <Button kind="danger" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }} onClick={() => setConfirmDelete(true)}>
            <Trash2 size={15} /> Delete bill
          </Button>
        ) : (
          <Button kind="danger" onClick={onDelete}>Tap again to delete for good</Button>
        )}
      </div>
    </div>
  );
};

// How many times this bill has been through, and when it was paid - grouped the
// same way the list groups them into one current row (see billKey).
const BillHistory = ({ bill, allBills }) => {
  const { tokens, fonts } = useTheme();
  const money = useMoney();
  const occurrences = allBills
    .filter((b) => billKey(b) === billKey(bill))
    .sort((a, b) => (a.dueDate < b.dueDate ? 1 : a.dueDate > b.dueDate ? -1 : 0));
  const paidCount = occurrences.filter((b) => b.status === "Paid").length;
  const paidTotal = occurrences.filter((b) => b.status === "Paid").reduce((s, b) => s + b.amount, 0);

  return (
    <div>
      <div style={{ marginBottom: 14, padding: 12, borderRadius: 12, backgroundColor: tokens.neutralSoft, fontSize: 13, color: tokens.textPrimary }}>
        Paid <strong>{paidCount}</strong> time{paidCount === 1 ? "" : "s"} · {money(paidTotal)} total
      </div>
      {occurrences.map((b, i) => (
        <div key={b.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderTop: i ? `1px solid ${tokens.surfaceBorder}` : "none" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: tokens.textPrimary }}>{formatDate(b.dueDate)}</div>
            {!!b.description && <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>{b.description}</div>}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontFamily: fonts.mono, fontSize: 14, fontWeight: 700, color: tokens.textPrimary }}>{money(b.amount)}</div>
            <div style={{ marginTop: 4 }}><StatusPill label={getBillDisplayStatus(b)} /></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// One sheet, three screens: view details (with Edit / History / Delete), the edit
// form, and the history list. Tapping a bill always lands on "view" first. Editing
// uses the same form as adding a Bill & Payment, filled in with this bill.
const BillSheet = ({ bill, allBills, data, onClose, onPay }) => {
  const [screen, setScreen] = useState("view");
  const isPaid = bill.status === "Paid";

  if (screen === "edit") return <AddEntrySheet mode="bill" bill={bill} data={data} onClose={onClose} onCancel={() => setScreen("view")} />;

  const titles = { view: bill.payee, history: `${bill.payee} history` };

  return (
    <Sheet title={titles[screen]} onClose={onClose}>
      {screen === "view" && (
        <BillView
          bill={bill}
          data={data}
          onMarkPaid={() => { if (isPaid) data.update("bills", bill.id, { status: "Pending" }); else onPay(bill); onClose(); }}
          onEdit={() => setScreen("edit")}
          onHistory={() => setScreen("history")}
          onDelete={() => { data.remove("bills", bill.id); onClose(); }}
        />
      )}
      {screen === "history" && <BillHistory bill={bill} allBills={allBills} />}
    </Sheet>
  );
};

const BillsScreen = ({ data }) => {
  const { tokens, fonts } = useTheme();
  const money = useMoney();
  const [filter, setFilter] = useState("All");
  const [selectedId, setSelectedId] = useState(null);
  const payBill = usePayBill(data);

  // Only repeating bills live here; one-time payments are expenses, in Activity.
  const bills = groupCurrentBills(data.bills.filter(isRepeatingBill))
    .filter((b) => filter === "All" || getBillDisplayStatus(b) === filter)
    .sort((a, b) => (a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0));

  const selected = selectedId && data.bills.find((b) => b.id === selectedId);

  return (
    <div>
      <ChipRow>
        {FILTERS.map((f) => <Chip key={f} active={filter === f} onClick={() => setFilter(f)}>{f}</Chip>)}
      </ChipRow>

      {bills.length === 0 && <Empty>No bills here yet. Tap + below to add one.</Empty>}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
        {bills.map((b) => (
          <Card key={b.id} style={{ padding: 0 }}>
            <button
              onClick={() => setSelectedId(b.id)}
              style={{ width: "100%", padding: 16, background: "none", border: "none", textAlign: "left", cursor: "pointer", color: tokens.textPrimary, display: "flex", gap: 12, alignItems: "flex-start" }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {b.payee}
                  {!!b.autopay && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 999, backgroundColor: tokens.accentSoft, color: tokens.accent }}>AUTO</span>}
                </div>
                <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 3 }}>{b.type} · {b.schedule}</div>
                {!!b.description && <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 3 }}>{b.description}</div>}
                <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 6 }}>{dueText(b)}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: fonts.mono, fontSize: 15, fontWeight: 700 }}>{billAmountText(b, money)}</div>
                <div style={{ marginTop: 6 }}><StatusPill label={getBillDisplayStatus(b)} /></div>
              </div>
            </button>
          </Card>
        ))}
      </div>

      {selected && <BillSheet key={selected.id} bill={selected} allBills={data.bills} data={data} onPay={payBill.pay} onClose={() => setSelectedId(null)} />}
      {payBill.sheet}
    </div>
  );
};

export default BillsScreen;
